'use client';

import { useMemo, useCallback } from 'react';
import { useTranslations } from 'next-intl';
import { useQuery } from '@tanstack/react-query';
import type { ColumnDef } from '@/lib/table-features';
import {
  parseAsArrayOf,
  parseAsInteger,
  parseAsString,
  useQueryState,
  useQueryStates
} from 'nuqs';
import type { ExtendedColumnSort } from '@/types/data-table';
import type { BulkPayload } from '@/types/bulk-action';
import { getSortingStateParser } from '@/lib/parsers';
import { buildDynamicFormColumns } from '@/lib/build-dynamic-form-columns';
import {
  Plus,
  Pencil,
  Trash2,
  Loader2,
  Archive,
  Eye,
  FileText,
  QrCode,
  CheckCircle
} from 'lucide-react';
import { Link } from '@/i18n/routing';

import { useDataTable } from '@/hooks/use-data-table';
import { useFilteredColumns } from '@/hooks/use-filtered-columns';
import { useSelectAllPages } from '@/hooks/use-select-all-pages';
import { useHashParamDialog } from '@/hooks/use-hash-dialog';
import { useRouter } from '@/i18n/routing';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { ListingSkeleton } from '@/components/listing-skeleton';
import { DataTable } from '@/components/ui/table/data-table';
import { DataTableToolbar } from '@/components/ui/table/data-table-toolbar';
import { DataTableColumnHeader } from '@/components/ui/table/data-table-column-header';
import { DataViewToggle, useDataView } from '@/components/data-view-toggle';
import { PaginationControls } from '@/components/pagination-controls';
import { SortControl } from '@/components/sort-control';
import {
  HighlightMatch,
  getColumnSearchTerms
} from '@/components/highlight-match';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog';
import { SelectAllBanner } from '@/components/select-all-banner';
import { PermissionGate } from '@/features/auth';
import { dynamicFormQueries, type DynamicForm } from '@/features/dynamic-forms';

import { registrationQueries } from '../services/registration.queries';
import {
  useAdvanceStageMutation,
  useDeleteRegistrationMutation
} from '../services/registration.mutations';
import { RegistrationFormDialog } from './registration-form-dialog';
import { RegistrationSuccessDialog } from './registration-success-dialog';
import { RegistrationsTableActionBar } from './registrations-table-action-bar';
import { RegistrationCard } from './registration-card';
import { RegistrationCompactRow } from './registration-compact-row';
import { REGISTRATION_HASH } from '../constants';
import {
  StagePill,
  SubStatusCell,
  getEffectiveStage
} from '../lib/status-stage-cells';
import type { Registration, RegistrationsListParams } from '../types';
import { stageFilterOptions, useWorkflowStages } from '@/features/workflow';

export function RegistrationListing() {
  const pipeline = useWorkflowStages();
  const t = useTranslations('registration');
  const tc = useTranslations('common');
  const router = useRouter();
  const completedStage = 'registration';
  const [view, setView] = useDataView('registration');
  // Overlay open-state lives in the URL hash (#edit/<id>, #qr/<id>,
  // #delete/<id>) so overlays are deep-linkable and close on browser Back.
  // Destructure the stable callbacks so they can go in hook dep arrays without
  // tripping the React Compiler's manual-memoization check.
  const {
    open: editOpen,
    param: editId,
    openWith: openEdit,
    onOpenChange: closeEdit
  } = useHashParamDialog(REGISTRATION_HASH.edit);
  const {
    open: qrOpen,
    param: qrId,
    openWith: openQr,
    onOpenChange: closeQr
  } = useHashParamDialog(REGISTRATION_HASH.qr);
  const {
    open: deleteOpen,
    param: deleteId,
    openWith: openDelete,
    onOpenChange: closeDelete
  } = useHashParamDialog(REGISTRATION_HASH.delete);

  // ─── Dynamic form selector ──────────────────────────────────────────
  const { data: activeTemplate, isLoading: isTemplateLoading } = useQuery(
    dynamicFormQueries.activeByType('registration')
  );
  const { data: allFormsData } = useQuery(
    dynamicFormQueries.list({
      type: 'registration',
      status: 'active',
      per_page: 100
    })
  );
  const allForms = (allFormsData?.data ?? []) as DynamicForm[];

  const [urlFormId, setUrlFormId] = useQueryState(
    'dynamic_form_id',
    parseAsString.withDefault('')
  );
  const activeFormId = activeTemplate?.data?.id;
  const isAllSelected = urlFormId === 'all';

  // Form used for columns/filters: active form when "all" is selected
  const displayFormId = isAllSelected
    ? activeFormId
    : urlFormId
      ? Number(urlFormId)
      : activeFormId;

  // Form passed to API: undefined when "all" is selected (show all records)
  const apiFormId = isAllSelected
    ? undefined
    : urlFormId
      ? Number(urlFormId)
      : activeFormId;

  // Fetch selected form detail (with fields) when not the active form
  const { data: selectedFormData } = useQuery({
    ...dynamicFormQueries.detail(displayFormId ?? 0),
    enabled: !!displayFormId && displayFormId !== activeFormId
  });

  const selectedForm =
    displayFormId === activeFormId
      ? activeTemplate?.data
      : selectedFormData?.data;
  const dynamicFormFields = useMemo(
    () => selectedForm?.fields ?? [],
    [selectedForm?.fields]
  );
  const dynamicColumns = useMemo(
    () => buildDynamicFormColumns(dynamicFormFields),
    [dynamicFormFields]
  );
  const toolbarKey = useMemo(
    () => dynamicColumns.map((c) => c.id).join(',') || 'base',
    [dynamicColumns]
  );

  const selectionFields = useMemo(
    () => dynamicFormFields.filter((f) => f.type === 'selection_list'),
    [dynamicFormFields]
  );
  const dynamicFieldParsers = useMemo(
    () =>
      Object.fromEntries(
        selectionFields.map((f) => [
          `form_field_${f.id}`,
          parseAsArrayOf(parseAsString, ',').withDefault([])
        ])
      ),
    [selectionFields]
  );
  const [dynamicFieldFilters] = useQueryStates(dynamicFieldParsers);

  const textFields = useMemo(
    () =>
      dynamicFormFields.filter(
        (f) =>
          f.type === 'short_text' ||
          f.type === 'long_text' ||
          f.type === 'phone' ||
          f.type === 'email'
      ),
    [dynamicFormFields]
  );

  const dateFields = useMemo(
    () => dynamicFormFields.filter((f) => f.type === 'date'),
    [dynamicFormFields]
  );
  const dynamicDateParsers = useMemo(
    () =>
      Object.fromEntries(
        dateFields.map((f) => [
          `form_field_${f.id}`,
          parseAsString.withDefault('')
        ])
      ),
    [dateFields]
  );
  const [dynamicDateFilters] = useQueryStates(dynamicDateParsers);

  const searchPlaceholder =
    textFields.length > 0
      ? `${t('searchPlaceholder')}, ${textFields.map((f) => f.label).join(', ')}...`
      : `${t('searchPlaceholder')}...`;

  const searchHint =
    textFields.length > 0
      ? t('searchHintWithFields', {
          fields: textFields.map((f) => f.label).join(', ')
        })
      : t('searchHint');

  const columns: ColumnDef<Registration>[] = [
    {
      id: 'select',
      header: ({ table }) => (
        <Checkbox
          checked={
            table.getIsAllPageRowsSelected() ||
            (table.getIsSomePageRowsSelected() && 'indeterminate')
          }
          onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
          aria-label='Select all'
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value) => row.toggleSelected(!!value)}
          aria-label='Select row'
        />
      ),
      enableSorting: false,
      enableHiding: false,
      size: 40
    },
    {
      id: 'container_number',
      accessorKey: 'container_number',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('containerNumber')} />
      ),
      cell: ({ row, column }) => (
        <Link
          href={`/dashboard/containers/${row.original.id}?stage=registration`}
          className='text-primary font-medium hover:underline'
        >
          <HighlightMatch
            text={row.original.container_number}
            query={getColumnSearchTerms(column)}
          />
        </Link>
      ),
      enableColumnFilter: true,
      meta: {
        label: searchPlaceholder,
        variant: 'text' as const,
        placeholder: searchPlaceholder,
        searchHint,
        permission: 'registrations.column.containerNumber'
      }
    },
    ...dynamicColumns,
    {
      id: 'review_status',
      header: t('reviewStatus'),
      cell: ({ row }) => {
        const reg = row.original;
        if (reg.rejected_to_stage) {
          const stageLabels: Record<string, string> = {
            registration: t('stageRegistration'),
            sticking: t('stageSticking')
          };
          return (
            <div className='flex flex-col gap-1'>
              <Badge
                variant='outline'
                className='border-orange-200 bg-orange-50 text-orange-700 dark:border-orange-800 dark:bg-orange-950 dark:text-orange-300'
                title={reg.last_rejection_reason ?? ''}
              >
                {t('returnedForCorrection')}
              </Badge>
              <span className='text-muted-foreground text-xs'>
                → {stageLabels[reg.rejected_to_stage] ?? reg.rejected_to_stage}
              </span>
            </div>
          );
        }
        if (reg.review_completed_at) {
          return (
            <Badge
              variant='outline'
              className='border-green-200 bg-green-50 text-green-700 dark:border-green-800 dark:bg-green-950 dark:text-green-300'
            >
              {t('approved')}
            </Badge>
          );
        }
        if (reg.review_started_at || reg.current_stage === 'review') {
          return (
            <Badge
              variant='outline'
              className='border-purple-200 bg-purple-50 text-purple-700 dark:border-purple-800 dark:bg-purple-950 dark:text-purple-300'
            >
              {t('inReview')}
            </Badge>
          );
        }
        return (
          <span className='text-muted-foreground text-sm'>
            {t('notReviewed')}
          </span>
        );
      },
      enableSorting: false,
      meta: { permission: 'registrations.column.reviewStatus' }
    },
    {
      id: 'rejection_count',
      accessorKey: 'rejection_count',
      header: t('rejectionCount'),
      cell: ({ row }) => {
        const count = row.original.rejection_count;
        if (!count) return null;
        return (
          <Badge
            variant='outline'
            className='border-red-200 bg-red-50 text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-300'
          >
            {count}
          </Badge>
        );
      },
      enableSorting: false
    },
    {
      id: 'current_stage',
      accessorKey: 'current_stage',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('currentStage')} />
      ),
      cell: ({ row }) => {
        const effectiveStage = getEffectiveStage(
          row.original.current_stage,
          row.original.stage_status
        );
        const camel = effectiveStage
          .split('_')
          .map((p, i) => (i === 0 ? p : p.charAt(0).toUpperCase() + p.slice(1)))
          .join('');
        const pascal = camel.charAt(0).toUpperCase() + camel.slice(1);
        return (
          <StagePill
            stage={effectiveStage}
            label={t(`stage${pascal}` as never)}
          />
        );
      },
      enableSorting: false,
      enableColumnFilter: true,
      meta: {
        label: t('filterStage'),
        variant: 'multiSelect' as const,
        icon: 'solar:routing-bold-duotone',
        options: stageFilterOptions(pipeline.stages, (key) => t(key)),
        permission: 'registrations.column.currentStage'
      }
    },
    {
      id: 'subStatus',
      accessorKey: 'stage_status',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('subStatus')} />
      ),
      cell: ({ row }) => (
        <SubStatusCell
          stageStatus={row.original.stage_status}
          physicalStage={row.original.physical_stage}
        />
      ),
      enableSorting: false,
      enableColumnFilter: true,
      meta: {
        label: t('filterStatus'),
        variant: 'multiSelect' as const,
        icon: 'solar:checklist-minimalistic-bold-duotone',
        options: [
          { label: t('stageStatus.creating'), value: 'creating' },
          {
            label: t('stageStatus.returned_from_sticking'),
            value: 'returned_from_sticking'
          },
          {
            label: t('stageStatus.returned_for_correction'),
            value: 'returned_for_correction'
          }
        ],
        permission: 'registrations.column.subStatus'
      }
    },
    {
      id: 'is_rejected',
      accessorFn: (row) => (row.rejected_to_stage !== null ? 'true' : 'false'),
      header: () => null,
      cell: () => null,
      size: 0,
      enableColumnFilter: true,
      enableSorting: false,
      enableHiding: false,
      meta: {
        label: tc('filterRejectionStatus'),
        variant: 'select' as const,
        icon: 'solar:close-circle-bold-duotone',
        options: [
          { label: tc('filterRejected'), value: 'true' },
          { label: tc('filterNotRejected'), value: 'false' }
        ]
      }
    },
    {
      id: 'actions',
      header: () => <div className='text-end'>{t('actions')}</div>,
      cell: ({ row }) => {
        const item = row.original;
        const canComplete =
          item.current_stage === 'registration' && !item.rejected_to_stage;
        return (
          <div className='flex items-center justify-end gap-1'>
            {canComplete && (
              <Button
                variant='ghost'
                size='icon'
                disabled={advanceMutation.isPending}
                onClick={() =>
                  advanceMutation.mutate({
                    id: item.id,
                    data: { from: completedStage }
                  })
                }
                title={t('completeRegistration')}
              >
                <CheckCircle className='h-4 w-4 text-green-600' />
              </Button>
            )}
            <Button
              variant='ghost'
              size='icon'
              onClick={() => openQr(String(item.id))}
            >
              <QrCode className='h-4 w-4' />
            </Button>
            <Button
              variant='ghost'
              size='icon'
              onClick={() =>
                router.push(
                  `/dashboard/containers/${item.id}?stage=registration`
                )
              }
            >
              <Eye className='h-4 w-4' />
            </Button>
            <PermissionGate permissions={['registrations.edit']}>
              <Button
                variant='ghost'
                size='icon'
                onClick={() => handleEdit(item)}
              >
                <Pencil className='h-4 w-4' />
              </Button>
            </PermissionGate>
            <PermissionGate permissions={['registrations.delete']}>
              <Button
                variant='ghost'
                size='icon'
                onClick={() => openDelete(String(item.id))}
              >
                <Trash2 className='text-destructive h-4 w-4' />
              </Button>
            </PermissionGate>
          </div>
        );
      },
      enableSorting: false,
      enableHiding: false
    }
  ];

  // Read URL state for query params
  const [urlPage] = useQueryState('page', parseAsInteger.withDefault(1));
  const [urlPerPage] = useQueryState('perPage', parseAsInteger.withDefault(10));
  const [urlSearch] = useQueryState(
    'container_number',
    parseAsArrayOf(parseAsString, ',').withDefault([])
  );
  const [urlStage, setUrlStage] = useQueryState(
    'current_stage',
    parseAsArrayOf(parseAsString, ',').withDefault(['registration'])
  );

  const [urlIsRejected] = useQueryState(
    'is_rejected',
    parseAsArrayOf(parseAsString, ',').withDefault([])
  );
  const [urlSubStatus] = useQueryState(
    'subStatus',
    parseAsArrayOf(parseAsString, ',').withDefault([])
  );
  const [urlSorting] = useQueryState(
    'sort',
    getSortingStateParser<Registration>().withDefault([
      { id: 'created_at', desc: true }
    ] as ExtendedColumnSort<Registration>[])
  );

  const queryParams = useMemo<RegistrationsListParams>(() => {
    const params: RegistrationsListParams = {
      page: urlPage,
      per_page: urlPerPage
    };
    if (urlSearch.length > 0) {
      params.search = urlSearch;
    }
    if (apiFormId) {
      params.dynamic_form_id = apiFormId;
    }
    if (urlStage.length > 0) {
      params.current_stage = urlStage.join(',');
    }
    params.sort_by = urlSorting[0]?.id ?? 'created_at';
    params.sort_direction = urlSorting[0]?.desc ? 'desc' : 'asc';

    if (urlIsRejected.includes('true')) {
      params.is_rejected = true;
    } else if (urlIsRejected.includes('false')) {
      params.is_rejected = false;
    }

    if (urlSubStatus.length > 0) {
      params.stage_status = urlSubStatus.join(',');
    }

    for (const [key, values] of Object.entries(dynamicFieldFilters)) {
      if (Array.isArray(values) && values.length > 0) {
        const fieldId = key.replace('form_field_', '');
        (params as Record<string, unknown>)[
          `form_responses_filter[${fieldId}][]`
        ] = values;
      }
    }

    if (urlSearch.length > 0 && textFields.length > 0) {
      (params as Record<string, unknown>)['search_text_field_ids'] = textFields
        .map((f) => f.id)
        .join(',');
    }

    for (const [key, value] of Object.entries(dynamicDateFilters)) {
      if (value) {
        const fieldId = key.replace('form_field_', '');
        const parts = String(value).split(',').filter(Boolean);
        const toDateStr = (ts: string) => {
          const d = new Date(Number(ts));
          return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        };
        const from = parts[0] ? toDateStr(parts[0]) : null;
        const to = parts[1] ? toDateStr(parts[1]) : null;
        if (from)
          (params as Record<string, unknown>)[
            `form_date_filter[${fieldId}][from]`
          ] = from;
        if (to)
          (params as Record<string, unknown>)[
            `form_date_filter[${fieldId}][to]`
          ] = to;
      }
    }

    return params;
  }, [
    urlPage,
    urlPerPage,
    urlSearch,
    apiFormId,
    urlStage,
    urlIsRejected,
    urlSubStatus,
    urlSorting,
    dynamicFieldFilters,
    textFields,
    dynamicDateFilters
  ]);

  const { data, isPending, isPlaceholderData } = useQuery(
    registrationQueries.list(queryParams)
  );
  const deleteMutation = useDeleteRegistrationMutation();
  const advanceMutation = useAdvanceStageMutation();

  const registrations = (data?.data ?? []) as Registration[];
  const pageCount = data?.pagination?.totalPages ?? 0;
  const pagination = data?.pagination;

  // Resolve the hash id param back to the loaded row (row must be on the
  // current page; deep-linking an off-page id simply won't open the overlay).
  const editTarget = editOpen
    ? (registrations.find((r) => String(r.id) === editId) ?? null)
    : null;
  const qrTarget = qrOpen
    ? (registrations.find((r) => String(r.id) === qrId) ?? null)
    : null;
  const deleteTarget = deleteOpen
    ? (registrations.find((r) => String(r.id) === deleteId) ?? null)
    : null;

  const filteredColumns = useFilteredColumns(columns);

  const { table } = useDataTable<Registration>({
    columns: filteredColumns,
    pageKey: 'registration',
    data: registrations,
    pageCount,
    initialState: {
      sorting: [{ id: 'created_at', desc: true }],
      pagination: { pageIndex: 0, pageSize: 10 }
    }
  });

  const selectAll = useSelectAllPages(table, pagination?.total ?? 0);

  const getBulkPayload = useCallback((): BulkPayload => {
    if (selectAll.isAllPagesSelected) {
      const { page, per_page, sort_by, sort_direction, ...filters } =
        queryParams;
      return { mode: 'all', filters };
    }
    const selectedIds = table
      .getFilteredSelectedRowModel()
      .rows.map((row) => row.original.id);
    return { mode: 'ids', ids: selectedIds };
  }, [selectAll.isAllPagesSelected, queryParams, table]);

  const handleBulkComplete = useCallback(() => {
    selectAll.reset();
  }, [selectAll]);

  const tableState = table.state;

  const sortOptions = useMemo(
    () => [
      { label: t('containerNumber'), value: 'container_number' },
      { label: t('createdAt'), value: 'created_at' },
      { label: t('currentStage'), value: 'current_stage' }
    ],
    [t]
  );

  const handleView = useCallback(
    (item: Registration) => {
      router.push(`/dashboard/containers/${item.id}?stage=registration`);
    },
    [router]
  );

  const handleQr = useCallback(
    (item: Registration) => openQr(String(item.id)),
    [openQr]
  );

  const handleDelete = useCallback(
    (item: Registration) => openDelete(String(item.id)),
    [openDelete]
  );

  function handleEdit(item: Registration) {
    openEdit(String(item.id));
  }

  function handleCreate() {
    router.push('/dashboard/registration/new');
  }

  async function confirmDelete() {
    if (!deleteTarget) return;
    try {
      await deleteMutation.mutateAsync(deleteTarget.id);
    } catch {
      // handled by global MutationCache
    }
    closeDelete(false);
  }

  return (
    <div className='space-y-6'>
      {/* Header */}
      <div className='flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between'>
        <div className='flex items-center gap-2'>
          <FileText className='text-muted-foreground h-4 w-4' />
          <Select
            value={urlFormId || (activeFormId ? String(activeFormId) : '')}
            onValueChange={(v) => {
              if (v === String(activeFormId)) {
                setUrlFormId(null);
              } else {
                setUrlFormId(v || null);
              }
            }}
          >
            <SelectTrigger className='h-8 w-auto min-w-[200px] text-xs'>
              <SelectValue placeholder={t('registrationForm')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value='all'>{t('allForms')}</SelectItem>
              {allForms.map((form) => (
                <SelectItem key={form.id} value={String(form.id)}>
                  {form.name}
                  {form.is_default && (
                    <span className='text-muted-foreground ms-1'>
                      ({t('defaultForm')})
                    </span>
                  )}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className='flex items-center gap-2'>
          {view !== 'table' && (
            <SortControl
              options={sortOptions}
              sorting={tableState.sorting}
              onSortingChange={(s) => table.setSorting(s)}
            />
          )}
          <DataViewToggle
            value={view}
            onValueChange={setView}
            pageKey='registration'
          />
          <PermissionGate permissions={['registrations.create']}>
            <Button onClick={handleCreate}>
              <Plus className='me-2 h-4 w-4' />
              {t('addNew')}
            </Button>
          </PermissionGate>
        </div>
      </div>

      {/* Data */}
      <DataTableToolbar
        key={toolbarKey}
        table={table}
        viewFilterPermission='registrations.view_filter'
      />

      {isPending || isTemplateLoading ? (
        <ListingSkeleton view={view} />
      ) : registrations.length === 0 &&
        tableState.columnFilters.length === 0 ? (
        <div className='flex flex-col items-center justify-center py-12 text-center'>
          <Archive className='text-muted-foreground mb-4 h-12 w-12' />
          <p className='text-muted-foreground text-lg'>
            {t('noRegistrations')}
          </p>
        </div>
      ) : view === 'grid' ? (
        <>
          <div
            className={cn(
              'grid gap-4 sm:grid-cols-2 lg:grid-cols-3',
              isPlaceholderData && 'opacity-60 transition-opacity'
            )}
          >
            {registrations.map((item) => (
              <RegistrationCard
                key={item.id}
                registration={item}
                dynamicFormFields={dynamicFormFields}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onView={handleView}
                onQr={handleQr}
              />
            ))}
          </div>
          {pagination && pagination.totalPages > 0 && (
            <PaginationControls
              page={pagination.page}
              pageCount={pagination.totalPages}
              perPage={pagination.per_page}
              total={pagination.total}
              onPageChange={(p) => table.setPageIndex(p - 1)}
              onPerPageChange={(s) => {
                table.setPageSize(s);
                table.setPageIndex(0);
              }}
            />
          )}
        </>
      ) : view === 'compact' ? (
        <>
          <div
            className={cn(
              'space-y-2',
              isPlaceholderData && 'opacity-60 transition-opacity'
            )}
          >
            {registrations.map((item) => (
              <RegistrationCompactRow
                key={item.id}
                registration={item}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onView={handleView}
                onQr={handleQr}
              />
            ))}
          </div>
          {pagination && pagination.totalPages > 0 && (
            <PaginationControls
              page={pagination.page}
              pageCount={pagination.totalPages}
              perPage={pagination.per_page}
              total={pagination.total}
              onPageChange={(p) => table.setPageIndex(p - 1)}
              onPerPageChange={(s) => {
                table.setPageSize(s);
                table.setPageIndex(0);
              }}
            />
          )}
        </>
      ) : (
        <div
          className={cn(
            'flex min-h-[500px] flex-col gap-2',
            isPlaceholderData && 'opacity-60 transition-opacity'
          )}
        >
          <SelectAllBanner
            isAllPagesSelected={selectAll.isAllPagesSelected}
            isCurrentPageFullySelected={selectAll.isCurrentPageFullySelected}
            currentPageCount={registrations.length}
            totalCount={pagination?.total ?? 0}
            pageSize={tableState.pagination.pageSize}
            onSelectAllPages={() => selectAll.setAllPagesSelected(true)}
            onClearSelection={() => selectAll.reset()}
          />
          <DataTable
            table={table}
            actionBar={
              <RegistrationsTableActionBar
                table={table}
                getBulkPayload={getBulkPayload}
                onComplete={handleBulkComplete}
                isAllPagesSelected={selectAll.isAllPagesSelected}
                totalCount={pagination?.total ?? 0}
              />
            }
          />
        </div>
      )}

      {/* QR dialog — #qr/<id> */}
      {qrOpen && qrTarget && (
        <RegistrationSuccessDialog
          containerNumber={qrTarget.container_number}
          registration={qrTarget}
          registrationId={qrTarget.id}
          onOpenChange={closeQr}
        />
      )}

      {/* Form dialog — #edit/<id> */}
      <RegistrationFormDialog
        open={editOpen && !!editTarget}
        onOpenChange={closeEdit}
        registration={editTarget}
        formId={
          isAllSelected
            ? (editTarget?.dynamic_form_id ?? undefined)
            : displayFormId
        }
        advanceFrom={completedStage}
      />

      {/* Delete confirmation — #delete/<id> */}
      <AlertDialog
        open={deleteOpen && !!deleteTarget}
        onOpenChange={closeDelete}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('deleteConfirmTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('deleteConfirmDescription', {
                number: deleteTarget?.container_number ?? ''
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className={cn(
                'bg-destructive text-destructive-foreground hover:bg-destructive/90'
              )}
            >
              {deleteMutation.isPending && (
                <Loader2 className='me-2 h-4 w-4 animate-spin' />
              )}
              {t('delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
